import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
	PAY_CATCHUP__ERROR_TOAST,
	PAY_CATCHUP__SUCCESS_TOAST,
} from '../components/catchUps/data/constants';
import { errorToast, successToast } from '../components/Toast';
import {
	PAY_INVOICE_FAILURE,
	PAY_INVOICE_SUCCESS,
	REVERSE_INVOICE_FAILURE,
	REVERSE_INVOICE_SUCCESS,
	WRITE_OFF_INVOICE_FAILURE,
	WRITE_OFF_INVOICE_SUCCESS,
} from '../data/labelsAndMessages';
import {
	CheckInvoicesResponse,
	FinancialTransaction,
	InvoicePaymentRequest,
	PaymentCreateRequest,
	PaymentResponse2,
	PaymentReversalReason,
	PolicyInvoice2Response,
} from '../types/policy';
import { PremiumReportingInvoice } from '../types/premiumReports';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

interface InvoiceApi {
	fetchInvoice(
		authorizationToken: string,
		invoiceLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyInvoice2Response>>;
	payInvoice(
		authorizationToken: string,
		invoiceLocator: string,
		paymentCreateReq: PaymentCreateRequest,
	): Promise<AxiosResponse<PaymentResponse2>>;
	reversePayment(
		authorizationToken: string,
		paymentLocator: string,
		reversalReason?: PaymentReversalReason,
	): Promise<AxiosResponse<PaymentResponse2>>;
	fetchFinancialTransactions(
		authorizationToken: string,
		invoiceLocator: string,
	): Promise<AxiosResponse<{ financialTransactions: FinancialTransaction[] }>>;
	fetchFutureAndGeneratedInvoices(
		authorizationToken: string,
		policyLocator: string,
	): Promise<AxiosResponse<CheckInvoicesResponse>>;
	writeOffInvoice(
		authorizationToken: string,
		invoiceLocator: string,
	): Promise<AxiosResponse<PolicyInvoice2Response | PremiumReportingInvoice>>;
}

const InvoiceApi = {
	fetchInvoice(
		authorizationToken: string,
		invoiceLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/invoice/${invoiceLocator}`,
			authorizationToken,
			config,
		);
	},
	payInvoice(
		authorizationToken: string,
		invoiceLocator: string,
		paymentCreateReq: InvoicePaymentRequest,
	) {
		return post(
			`${getApiUrl()}/invoice/${invoiceLocator}/pay`,
			authorizationToken,
			paymentCreateReq,
		);
	},
	reversePayment(
		authorizationToken: string,
		paymentLocator: string,
		reversalReason: PaymentReversalReason,
	) {
		return post(
			`${getApiUrl()}/payments/${paymentLocator}/reverse`,
			authorizationToken,
			reversalReason,
		);
	},
	fetchFinancialTransactions(
		authorizationToken: string,
		invoiceLocator: string,
	) {
		return get(
			`${getApiUrl()}/invoice/${invoiceLocator}/financialTransactions`,
			authorizationToken,
		);
	},
	fetchFutureAndGeneratedInvoices(
		authorizationToken: string,
		policyLocator: string,
	) {
		return get(
			`${getApiUrl()}/policy/${policyLocator}/checkInvoices`,
			authorizationToken,
		);
	},
	writeOffInvoice(authorizationToken: string, invoiceLocator: string) {
		return patch(
			`${getApiUrl()}/invoice/${invoiceLocator}/writeOff`,
			authorizationToken,
		);
	},
} as InvoiceApi;

export const getUseInvoiceQueryKey = (
	authToken: string,
	invoiceLocator: string,
) => ['invoice', invoiceLocator, authToken];

export const useInvoice = (authToken: string, invoiceLocator: string) => {
	const queryKey = getUseInvoiceQueryKey(authToken, invoiceLocator);
	return useQuery({
		queryKey: queryKey,
		queryFn: () => InvoiceApi.fetchInvoice(authToken, invoiceLocator),
		enabled: !!invoiceLocator,
	});
};

export const getUseTransactionsForInvoiceQueryKey = (
	authToken: string,
	invoiceLocator: string,
) => ['transactions', invoiceLocator, authToken];

export const useTransactionsForInvoice = (
	authToken: string,
	invoiceLocator: string,
) => {
	const queryKey = getUseTransactionsForInvoiceQueryKey(
		authToken,
		invoiceLocator,
	);
	return useQuery({
		queryKey,
		queryFn: () =>
			InvoiceApi.fetchFinancialTransactions(authToken, invoiceLocator),
		enabled: !!invoiceLocator,
	});
};

export const useWriteOffInvoiceMutation = (
	authToken: string,
	queryClient: QueryClient,
) => {
	return useMutation({
		mutationFn: ({ invoiceLocator }: { invoiceLocator: string }) =>
			InvoiceApi.writeOffInvoice(authToken, invoiceLocator),
		onSuccess: (response) => {
			const queryKey = getUseInvoiceQueryKey(authToken, response.data.locator);
			queryClient.invalidateQueries(queryKey);
			successToast(WRITE_OFF_INVOICE_SUCCESS());
		},
		onError: () => errorToast(WRITE_OFF_INVOICE_FAILURE()),
	});
};

export const usePayInvoiceMutation = (
	authToken: string,
	queryClient: QueryClient,
) =>
	useMutation({
		mutationFn: ({
			paymentCreateReq,
			invoiceLocator,
			isCatchUpInvoice,
		}: {
			paymentCreateReq: InvoicePaymentRequest;
			invoiceLocator: string;
			isCatchUpInvoice?: boolean;
		}) => InvoiceApi.payInvoice(authToken, invoiceLocator, paymentCreateReq),
		onSuccess: (response, request) => {
			const queryKey = getUseInvoiceQueryKey(
				authToken,
				response.data.invoiceLocator,
			);
			queryClient.invalidateQueries(queryKey);
			successToast(
				request.isCatchUpInvoice
					? PAY_CATCHUP__SUCCESS_TOAST
					: PAY_INVOICE_SUCCESS(),
			);
		},
		onError: (error, request) => {
			if (axios.isAxiosError(error)) {
				errorToast(`
				${
					request.isCatchUpInvoice
						? PAY_CATCHUP__ERROR_TOAST
						: PAY_INVOICE_FAILURE()
				} ${error.response?.data.message}`);
			}
		},
	});

export const useReverseInvoiceMutation = (
	authToken: string,
	queryClient: QueryClient,
) =>
	useMutation({
		mutationFn: ({
			paymentLocator,
			reversalReason,
		}: {
			paymentLocator: string;
			reversalReason: PaymentReversalReason;
		}) => InvoiceApi.reversePayment(authToken, paymentLocator, reversalReason),
		onSuccess: (response) => {
			const queryKey = getUseInvoiceQueryKey(response.data.locator, authToken);
			queryClient.invalidateQueries(queryKey);
			successToast(REVERSE_INVOICE_SUCCESS());
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(REVERSE_INVOICE_FAILURE() + error.response?.data.message);
			}
		},
	});

export const getFutureAndGeneratedInvoicesQueryKey = (
	policyLocator: string,
	authToken: string,
) => ['invoices', policyLocator, authToken];

export const useFutureAndGeneratedInvoices = (
	policyLocator: string,
	authToken: string,
	hasPolicyFetchPermission = false,
) =>
	useQuery({
		queryKey: getFutureAndGeneratedInvoicesQueryKey(policyLocator, authToken),
		queryFn: async () => {
			const response = await InvoiceApi.fetchFutureAndGeneratedInvoices(
				authToken,
				policyLocator,
			);
			return response.data;
		},
		enabled: !!policyLocator && hasPolicyFetchPermission,
	});

export default InvoiceApi;
